import { useCallback, useRef, useState } from 'react';

type ValidatorResult<T extends File, E> = {
  validFiles: T[];
  errors: E;
};

type Validator<T extends File, E> = (
  newFiles: FileList,
  existingFiles: T[],
) => ValidatorResult<T, E>;

type UseDropzoneOptions<T extends File, E> = {
  validator?: Validator<T, E>;
  onValidationError?: (errors: E) => void;
  onFilesAdded?: (files: T[]) => void;
};

type FileValidationOptions = {
  maxFileSize?: number;
  preventDuplicateNames?: boolean;
};

type FileValidationErrors = {
  duplicateNames: string[];
  oversizedFiles: string[];
};

type FileValidationResult = {
  validFiles: File[];
  errors: FileValidationErrors;
};

type ResultsReduced = {
  validFiles: File[];
  duplicateNames: string[];
  oversizedFiles: string[];
};

export const bulkUploadFileValidator =
  (options: FileValidationOptions = {}) =>
  (newFiles: FileList, existingFiles: File[]): FileValidationResult => {
    const { maxFileSize, preventDuplicateNames = true } = options;

    const validationResults = Array.from(newFiles).reduce<ResultsReduced>(
      (acc, file) => {
        if (preventDuplicateNames && existingFiles.some((f) => f.name === file.name)) {
          return {
            ...acc,
            duplicateNames: [...acc.duplicateNames, file.name],
          };
        }
        if (maxFileSize && file.size > maxFileSize) {
          return {
            ...acc,
            oversizedFiles: [...(acc.oversizedFiles || []), file.name],
          };
        }
        return {
          ...acc,
          validFiles: [...acc.validFiles, file],
        };
      },
      {
        duplicateNames: [],
        oversizedFiles: [],
        validFiles: [],
      },
    );

    const { validFiles, ...validationErrors } = validationResults;
    return {
      errors: { ...validationErrors },
      validFiles,
    };
  };

export const useFileDropzone = <T extends File, E>({
  validator,
  onFilesAdded,
  onValidationError,
}: UseDropzoneOptions<T, E>) => {
  const [files, setFiles] = useState<T[]>([]);
  const [validatorErrors, setValidatorErrors] = useState<E | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSetFiles = useCallback(
    (newFiles: FileList) => {
      const result = validator
        ? validator(newFiles, files)
        : { errors: undefined, validFiles: Array.from(newFiles) as T[] };

      setFiles((prevFiles) => [...prevFiles, ...result.validFiles]);

      if (result.errors) {
        setValidatorErrors(result.errors);
        onValidationError?.(result.errors);
      }

      if (result.validFiles.length > 0 && onFilesAdded) {
        onFilesAdded(result.validFiles);
      }
    },
    [validator, files, onValidationError, onFilesAdded],
  );

  const handleDrag = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const dragEvents = ['dragenter', 'dragover', 'dragleave'];
    if (!dragEvents.includes(e.type)) {
      return;
    }
    if (e.type === 'dragenter' || e.type === 'dragover') {
      return setDragActive(true);
    }
    if (e.type === 'dragleave') {
      return setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      setDragActive(false);

      if (!e.dataTransfer.files || !e.dataTransfer.files.length) {
        return;
      }
      handleSetFiles(e.dataTransfer.files);
    },
    [handleSetFiles],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();

      if (!e.target.files || !e.target.files.length) {
        return;
      }
      handleSetFiles(e.target.files);
    },
    [handleSetFiles],
  );

  const handleRemoveFile = useCallback(
    (file: T) =>
      setFiles((prevFiles) => {
        const newFiles = prevFiles.filter((f) => f !== file);
        if (inputRef.current && inputRef.current.files) {
          const dt = new DataTransfer();
          Array.from(inputRef.current.files).forEach((f) => {
            if (f.name !== file.name) {
              dt.items.add(file);
            }
          });
          inputRef.current.files = dt.files;
        }

        return newFiles;
      }),
    [],
  );

  const openFileDialog = useCallback(() => {
    if (!inputRef.current) {
      return;
    }
    inputRef.current.click();
  }, []);

  const resetFiles = useCallback(() => {
    setFiles([]);
    if (!inputRef.current) {
      return;
    }
    inputRef.current.value = '';
  }, []);

  return {
    dragActive,
    files,
    handleChange,
    handleDrag,
    handleDrop,
    handleRemoveFile,
    inputRef,
    openFileDialog,
    resetFiles,
    validatorErrors,
  };
};
